import React, { useState, useEffect, useMemo } from 'react';
import {
  Calendar,
  ChevronDown,
  ChevronRight,
  Download
} from 'lucide-react';
import {
  BroadcastStats,
  successRate,
  readRate,
  responseRate
} from '../../types/models/whatsapp';
import { apiClient } from '../../services/whatsapp/apiClient';

interface DailyStat {
  date: string;
  total?: number;
  sent?: number;
  delivered?: number;
  read?: number;
  failed?: number;
  responded?: number;
  byTemplate?: Record<
    string,
    {
      name?: string;
      category?: string;
      total?: number;
      sent?: number;
      delivered?: number;
      read?: number;
      failed?: number;
      responded?: number;
    }
  >;
}

const WhatsAppAnalytics: React.FC = () => {
  const [stats, setStats] = useState<DailyStat[]>([]);

  // MTD dates
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  const [fromDate, setFromDate] = useState(firstDay.toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(today.toISOString().split('T')[0]);

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchStats = async () => {
      setError(null);
      try {
        // Via apiClient, not a bare fetch: it attaches a live Firebase ID
        // token. The previous localStorage.getItem('token') was always empty —
        // Firebase Auth does not store tokens there — so every request went
        // out unauthenticated.
        const data = await apiClient.get<DailyStat[]>(
          `/analytics?from=${fromDate}&to=${toDate}`
        );
        if (!cancelled) setStats(Array.isArray(data) ? data : []);
      } catch (err: any) {
        if (cancelled) return;
        console.error('❌ Failed to fetch analytics:', err);
        setError(
          err?.status === 401 || err?.status === 403
            ? 'You do not have permission to view analytics.'
            : err?.message || 'Could not load analytics. Is the API reachable?'
        );
        setStats([]);
      }
    };

    fetchStats();
    return () => {
      cancelled = true;
    };
  }, [fromDate, toDate]);

  const aggregate = useMemo(() => {
    const agg: BroadcastStats = {
      total: 0,
      waiting: 0,
      sent: 0,
      delivered: 0,
      read: 0,
      failed: 0,
      responded: 0,
      skipped: 0,
      clicked: 0,
      clickedAndResponded: 0
    };
    stats.forEach((day) => {
      agg.total += day.total || 0;
      agg.sent += day.sent || 0;
      agg.delivered += day.delivered || 0;
      agg.read += day.read || 0;
      agg.failed += day.failed || 0;
      agg.responded += day.responded || 0;
    });
    return agg;
  }, [stats]);

  const templateAgg = useMemo(() => {
    const map = new Map<
      string,
      BroadcastStats & { category: string; label: string }
    >();
    stats.forEach((day) => {
      if (day.byTemplate) {
        Object.entries(day.byTemplate).forEach(([name, data]) => {
          if (!map.has(name)) {
            map.set(name, {
              // The map key is a sanitized field-path-safe key, not the
              // template name; prefer the real name recorded alongside it.
              label: data.name || name,
              category: data.category || 'UTILITY',
              total: 0,
              waiting: 0,
              sent: 0,
              delivered: 0,
              read: 0,
              failed: 0,
              responded: 0,
              skipped: 0,
              clicked: 0,
              clickedAndResponded: 0
            });
          }
          const tAgg = map.get(name)!;
          if (data.name) tAgg.label = data.name;
          if (data.category) tAgg.category = data.category;
          tAgg.total += data.total || 0;
          tAgg.sent += data.sent || 0;
          tAgg.delivered += data.delivered || 0;
          tAgg.read += data.read || 0;
          tAgg.failed += data.failed || 0;
          tAgg.responded += data.responded || 0;
        });
      }
    });
    return Array.from(map.entries()).map(([name, data]) => ({ name, ...data }));
  }, [stats]);

  const categoryAgg = useMemo(() => {
    const cats = {
      MARKETING: { total: 0, sent: 0, delivered: 0, read: 0, responded: 0 },
      UTILITY: { total: 0, sent: 0, delivered: 0, read: 0, responded: 0 },
      AUTHENTICATION: { total: 0, sent: 0, delivered: 0, read: 0, responded: 0 }
    };
    templateAgg.forEach((t) => {
      const cat = (t.category || 'UTILITY') as keyof typeof cats;
      if (cats[cat]) {
        cats[cat].total += t.total;
        cats[cat].sent += t.sent;
        cats[cat].delivered += t.delivered;
        cats[cat].read += t.read;
        cats[cat].responded += t.responded;
      }
    });
    return cats;
  }, [templateAgg]);

  const [expandedCategories, setExpandedCategories] = useState(true);

  return (
    <div className="flex h-full flex-col bg-gray-50 text-gray-900 wa-theme">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4 dark:border-gray-800 dark:bg-gray-900">
        <div>
          <h1 className="text-xl font-semibold dark:text-gray-100">
            Template Analytics
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Performance across all template sends
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 dark:border-gray-700 dark:bg-gray-800">
            <Calendar className="h-4 w-4 text-gray-500" />
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="bg-transparent text-sm font-medium outline-none dark:text-gray-100"
            />
            <span className="text-gray-400">-</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="bg-transparent text-sm font-medium outline-none dark:text-gray-100"
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {error && (
          <div className="mb-4 flex items-start justify-between gap-3 rounded-lg bg-danger-50 px-4 py-3 text-sm text-danger-600">
            <span>{error}</span>
            <button
              type="button"
              onClick={() => setError(null)}
              className="shrink-0 font-medium underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* KPI Tiles */}
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Total Messages
            </p>
            <p className="mt-2 text-3xl font-bold dark:text-white">
              {aggregate.total.toLocaleString()}
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Success Rate
            </p>
            <p className="mt-2 text-3xl font-bold text-success-600 dark:text-success-500">
              {(successRate(aggregate) * 100).toFixed(1)}%
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Read Rate
            </p>
            <p className="mt-2 text-3xl font-bold text-primary-600 dark:text-primary-500">
              {(readRate(aggregate) * 100).toFixed(1)}%
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Response Rate
            </p>
            <p className="mt-2 text-3xl font-bold text-purple-600 dark:text-purple-500">
              {(responseRate(aggregate) * 100).toFixed(1)}%
            </p>
          </div>
        </div>

        {/* Collapsible Category Summary */}
        <div className="mb-6 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <button
            onClick={() => setExpandedCategories(!expandedCategories)}
            className="flex w-full items-center justify-between bg-gray-50 px-5 py-4 hover:bg-gray-100 dark:bg-gray-800/50 dark:hover:bg-gray-800"
          >
            <div className="flex items-center gap-2 font-medium dark:text-gray-100">
              {expandedCategories ? (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronRight className="h-5 w-5 text-gray-500" />
              )}
              Category Summary
            </div>
          </button>
          {expandedCategories && (
            <div className="grid grid-cols-3 divide-x divide-gray-200 border-t border-gray-200 dark:divide-gray-800 dark:border-gray-800">
              {Object.entries(categoryAgg).map(([cat, data]) => (
                <div key={cat} className="p-5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {cat}
                  </p>
                  <div className="mt-4 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">Total</span>
                      <span className="font-medium dark:text-gray-100">{data.total}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">Success</span>
                      <span className="font-medium text-success-600 dark:text-success-500">
                        {data.total ? ((data.delivered / data.total) * 100).toFixed(1) : 0}%
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">Read</span>
                      <span className="font-medium text-primary-600 dark:text-primary-500">
                        {data.delivered ? ((data.read / data.delivered) * 100).toFixed(1) : 0}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Per-template Table */}
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-800">
            <h2 className="font-medium dark:text-gray-100">Templates</h2>
            <button className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
              <Download className="h-4 w-4" />
              Export CSV
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500 dark:bg-gray-800/50 dark:text-gray-400">
                <tr>
                  <th className="px-5 py-3 font-medium">Template</th>
                  <th className="px-5 py-3 font-medium">Category</th>
                  <th className="px-5 py-3 text-right font-medium">Total</th>
                  <th className="px-5 py-3 text-right font-medium">Sent</th>
                  <th className="px-5 py-3 text-right font-medium">Delivered</th>
                  <th className="px-5 py-3 text-right font-medium">Read</th>
                  <th className="px-5 py-3 text-right font-medium">Responded</th>
                  <th className="px-5 py-3 text-right font-medium">Failed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                {templateAgg.map((t) => (
                  <tr key={t.name} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-5 py-3 font-medium text-gray-900 dark:text-gray-100">
                      {t.label}
                    </td>
                    <td className="px-5 py-3">
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                        {t.category}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right text-gray-500 dark:text-gray-400">{t.total}</td>
                    <td className="px-5 py-3 text-right text-gray-500 dark:text-gray-400">{t.sent}</td>
                    <td className="px-5 py-3 text-right text-gray-500 dark:text-gray-400">{t.delivered}</td>
                    <td className="px-5 py-3 text-right text-gray-500 dark:text-gray-400">{t.read}</td>
                    <td className="px-5 py-3 text-right text-gray-500 dark:text-gray-400">{t.responded}</td>
                    <td className="px-5 py-3 text-right text-danger-600 dark:text-danger-500">{t.failed}</td>
                  </tr>
                ))}
                {templateAgg.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-5 py-8 text-center text-gray-500">
                      No template data for this period
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppAnalytics;
